import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import MyProfilePage from './components/MyProfilePage';
import ProfileModal from './components/ProfileModal';
import HomePage from './components/HomePage';
import Header from './components/Header';
import MyReviewsPage from './components/MyReviewsPage';
import {
  Star, MessageCircle, Users, Search,
  ChevronRight, ChevronDown, User, Bell, Heart, Share2,
  Shield, Verified, MapPin, Calendar, Clock, DollarSign,
  Mail, Instagram, Globe, Bookmark,
  CheckCircle, Info,
  Target, Trophy, Crown,
  PlusCircle, Edit3, Camera,
  BarChart3, Activity, X, LogOut,
  Loader, AlertTriangle, Copy
} from 'lucide-react';
import VerificationNotification from './components/VerificationNotification';
import { EnhancedDropdown } from './components/EnhancedDropdown';
import { EnhancedUserDropdown } from './components/EnhancedUserDropdown';
import { QuickReviewFlow } from './components/QuickReviewFlow';
import { ReviewSystem } from './components/ReviewSystem';
import { ServiceProviderSystem } from './components/ServiceProviderSystem';
import InstagramCallback from './components/InstagramCallback';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import SignUpPage from './components/SignUpPage';
import { handleInstagramLogin as sdkInstagramLogin, checkLoginStatus, logout } from './api/instagram';
import {
  initiateInstagramLogin,
  getInstagramUser,
  logoutInstagramUser,
  handleInstagramLoginFlow
} from './api/instagramLogin';
import unifiedAuthService from './services/unifiedAuth';
import VerificationSteps from "./components/VerificationSteps";
import { serviceTypes, categories, regions, reviewTypes, mockServices } from './constants';
import ProfileImage from './components/ProfileImage';

// Centralized API base URL for Azure Functions backend
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071';

// Copy Button Component for verification codes
const CopyButton = ({ code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
    >
      <Copy size={16} />
      {copied ? 'Copied!' : 'Copy Code'}
    </button>
  );
};

function App() {

  const navigate = useNavigate();

  const [services] = useState(mockServices)
  // Core Navigation & Authentication
  const [currentPage, setCurrentPage] = useState('home');

  // Verification Notification State
  const [verificationNotification, setVerificationNotification] = useState(null);

  // ===============================
  // UNIFIED AUTHENTICATION STATE
  // ===============================
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showInstagramCallback, setShowInstagramCallback] = useState(false);
  const [callbackProcessed, setCallbackProcessed] = useState(false);

  // New Authentication States (maintained for UI compatibility)
  const [isVerified, setIsVerified] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [authMode, setAuthMode] = useState('signin'); // 'signin', 'signup', 'verify'
  const [userReviews, setUserReviews] = useState([]);
  const [showUserReviews, setShowUserReviews] = useState(false);

  // Enhanced Verification States (from Case 7)
  const [verificationMethod, setVerificationMethod] = useState(null); // 'instagram', 'phone', 'email'
  const [verifyUsername, setVerifyUsername] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [isUserVerified, setIsUserVerified] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [skippedVerification, setSkippedVerification] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  const [isLookingUpUser, setIsLookingUpUser] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const [isSavingReview, setIsSavingReview] = useState(false);
  const [reviewSaveSuccess, setReviewSaveSuccess] = useState(false);

  // Form States
  const [formData, setFormData] = useState({
    name: '',
    familyName: '',
    email: '',
    phone: '',
    instagram: '',
    password: '',
    confirmPassword: ''
  });
  const [verificationData, setVerificationData] = useState({
    contact: '',
    code: ''
  });
  const [authData, setAuthData] = useState({ contact: '', password: '' });
  const [authError, setAuthError] = useState('');

  // Onboarding & Tutorial
  const [isFirstVisit] = useState(false);
  const [showTutorial] = useState(false);
  const [tutorialStep] = useState(0);
  const [userPreferences] = useState({
    interests: [],
    region: '',
    serviceTypes: [],
    budgetRange: ''
  });

  // Advanced UI States
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([
    {
      message: "Your profile was updated successfully.",
      timestamp: new Date(Date.now() - 1000 * 60 * 5).toLocaleString()
    },
    {
      message: "New review received for your service.",
      timestamp: new Date(Date.now() - 1000 * 60 * 30).toLocaleString()
    },
    {
      message: "Welcome to IGReview! Start by updating your profile.",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toLocaleString()
    }
  ]);
  const [darkMode] = useState(false);
  // const [viewMode, setViewMode] = useState('grid'); // grid, list, map

  // Search & Discovery
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    category: 'all',
    region: 'all',
    reviewType: 'all',
    priceRange: 'all',
    rating: 'all',
    verified: false,
    availability: 'all'
  });

  // Review & Rating System
  const [showReviewSystem, setShowReviewSystem] = useState(false);
  const [showQuickReview, setShowQuickReview] = useState(false);
  const [selectedServiceForReview, setSelectedServiceForReview] = useState(null);
  const [showProviderSystem, setShowProviderSystem] = useState(false);

  // Social Features
  const [followedProviders, setFollowedProviders] = useState([]);
  const [bookmarkedServices, setBookmarkedServices] = useState([]);
  // const [reviewLikes, setReviewLikes] = useState({});

  // Profile & Detailed Views
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showMyProfileModal, setShowMyProfileModal] = useState(false);
  const [profileReviews, setProfileReviews] = useState([]);


  const [activeProfileTab, setActiveProfileTab] = useState('overview');

  // Leaderboard State
  const [leaderboardView, setLeaderboardView] = useState('overall'); // overall, category, region
  const [sortBy, setSortBy] = useState('rating'); // rating, reviews, followers

  // Enhanced filtering logic with best-rated default sorting
  const filteredServices = services.filter(service => {
    const categoryMatch = activeFilters.category === 'all' || service.category === activeFilters.category;
    const regionMatch = activeFilters.region === 'all' || service.region === activeFilters.region;

    // Enhanced search matching including service types
    const searchMatch = !searchQuery ||
      service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.specialties.some(spec => spec.toLowerCase().includes(searchQuery.toLowerCase())) ||
      service.serviceTypes?.some(type => type.toLowerCase().includes(searchQuery.toLowerCase()));

    const verifiedMatch = !activeFilters.verified || service.verified;
    const ratingMatch = activeFilters.rating === 'all' || service.rating >= parseFloat(activeFilters.rating);

    // Review type filtering - check if service has good ratings for specific review type
    const reviewTypeMatch = activeFilters.reviewType === 'all' ||
      (service.reviewTypes && service.reviewTypes[activeFilters.reviewType]?.count > 0);

    // Price range filtering (if implemented)
    const priceMatch = activeFilters.priceRange === 'all' || true; // TODO: Implement price filtering

    // Availability filtering (if implemented)
    const availabilityMatch = activeFilters.availability === 'all' || true; // TODO: Implement availability filtering

    return categoryMatch && regionMatch && searchMatch && verifiedMatch && ratingMatch && reviewTypeMatch && priceMatch && availabilityMatch;
  }).sort((a, b) => {
    // Enhanced sorting based on review type filter
    if (activeFilters.reviewType !== 'all' && a.reviewTypes && b.reviewTypes) {
      const aRating = a.reviewTypes[activeFilters.reviewType]?.rating || 0;
      const bRating = b.reviewTypes[activeFilters.reviewType]?.rating || 0;

      if (bRating !== aRating) {
        return bRating - aRating;
      }

      const aCount = a.reviewTypes[activeFilters.reviewType]?.count || 0;
      const bCount = b.reviewTypes[activeFilters.reviewType]?.count || 0;
      return bCount - aCount;
    }

    // Default sorting by overall rating and review count
    if (b.rating !== a.rating) {
      return b.rating - a.rating;
    }
    return b.reviewCount - a.reviewCount;
  });

  // Add these state variables to App.js
  // const [searchSuggestions, setSearchSuggestions] = useState([]);

  // // Add this function to App.js
  // const handleSearchChange = (value) => {
  //   setSearchQuery(value);
  //   if (value.length > 2 && businesses.length > 0) {
  //     const suggestions = businesses
  //       .filter(s =>
  //         s?.searchData?.username?.toLowerCase().includes(value.toLowerCase()) ||
  //         s?.searchData?.full_name?.toLowerCase().includes(value.toLowerCase()) ||
  //         s?.specialties?.some(spec => spec.toLowerCase().includes(value.toLowerCase()))
  //       )
  //       .slice(0, 5)
  //       .map(s => ({
  //         type: 'service',
  //         text: s?.searchData?.full_name,
  //         username: s?.searchData?.username,
  //         data: s
  //       }));

  //     console.log('Suggestions:', suggestions);
  //     setSearchSuggestions(suggestions);
  //   } else {
  //     setSearchSuggestions([]);
  //     setSearchResult([]);
  //   }
  // };


  // User Experience Functions

  const handleServiceBookmark = (serviceId) => {
    setBookmarkedServices(prev =>
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleProviderFollow = (username) => {
    setFollowedProviders(prev =>
      prev.includes(username)
        ? prev.filter(u => u !== username)
        : [...prev, username]
    );
  };

  const openServiceProfile = (service, reviews = []) => {
    console.log('Opening service profile:', reviews);
    setSelectedProfile(service);
    setShowProfileModal(true);
    setProfileReviews(reviews);
    setActiveProfileTab('overview');
  };

  const startReviewFlow = (service) => {
    setSelectedServiceForReview(service);
    setShowQuickReview(true);
  };

  // Load user data on component mount
  useEffect(() => {
    const savedBookmarks = localStorage.getItem('bookmarkedServices');
    const savedFollows = localStorage.getItem('followedProviders');

    if (savedBookmarks) {
      setBookmarkedServices(JSON.parse(savedBookmarks));
    }
    if (savedFollows) {
      setFollowedProviders(JSON.parse(savedFollows));
    }
  }, []);

  // ===============================
  // UNIFIED AUTHENTICATION INITIALIZATION
  // ===============================
  useEffect(() => {
    // Add global debug functions for authentication cleanup
    window.clearAllAuth = () => {
      console.log('🧹 Clearing all authentication data...');
      unifiedAuthService.forceCleanAuth();
    };

    window.debugAuth = () => {
      console.log('🔍 Current auth state:', {
        isAuthenticated: unifiedAuthService.isAuthenticated(),
        currentUser: unifiedAuthService.getCurrentUser(),
        hasToken: !!unifiedAuthService.getAuthToken(),
        localStorage: {
          token: localStorage.getItem('igreview_auth_token'),
          user: localStorage.getItem('igreview_user_data'),
          oldInstagram: localStorage.getItem('instagramUser')
        }
      });
    };

    const initializeAuth = async () => {
      setAuthLoading(true);

      try {
        // Check for conflicting old authentication data and clear if found
        const hasOldInstagramData = localStorage.getItem('instagramUser') || localStorage.getItem('instagram_user');
        if (hasOldInstagramData && !unifiedAuthService.isAuthenticated()) {
          console.log('🧹 Found old Instagram data without unified auth, cleaning...');
          unifiedAuthService.clearStorage();
        }

        // Check if user is already authenticated with unified system
        if (unifiedAuthService.isAuthenticated()) {
          console.log('🔄 Validating existing authentication...');
          console.log('🔍 Current token:', unifiedAuthService.getAuthToken() ? 'Present' : 'Missing');
          console.log('🔍 Current user:', unifiedAuthService.getCurrentUser());

          const validationResult = await unifiedAuthService.validateToken();
          // console.log('🔍 Validation result:', validationResult);

          if (validationResult.success) {
            // Map all user data to a consistent profile structure
            const profileData = {
              userId: validationResult.user.userId,
              name: validationResult.user.profile?.name || null,
              familyName: validationResult.user.profile?.familyName || null,
              fullName: validationResult.user.profile?.name ? `${validationResult.user.profile.name} ${validationResult.user.profile.familyName || ''}`.trim() : null,
              email: validationResult.user.identifiers?.email || null,
              phone: validationResult.user.identifiers?.phone || null,
              instagramUsername: validationResult.user.identifiers?.instagramUsername || null,
              facebookId: validationResult.user.identifiers?.facebookId || null,
              verificationStatus: validationResult.user.verificationStatus || {},
              profilePicture: validationResult.user.profile?.avatar || null,
              displayName: validationResult.user.profile?.displayName || null,
              createdAt: validationResult.user.createdAt,
              lastLogin: validationResult.user.lastLogin
            };

            setUserProfile(profileData);
            setIsLoggedIn(true);
            setIsVerified(unifiedAuthService.hasAnyVerification());
            console.log('✅ Authentication validated:', profileData);

            // Fetch user reviews
            // console.log('🔄 Fetching user reviews...');
            const reviewsResult = await unifiedAuthService.getUserReviews();
            // console.log('🔍 Reviews result:', reviewsResult);

            if (reviewsResult.success) {
              setUserReviews(reviewsResult.reviews);
              // console.log('✅ User reviews loaded:', reviewsResult.reviews);
            } else {
              console.warn('⚠️ Failed to load user reviews:', reviewsResult.error);
            }
          } else {
            console.log('❌ Token validation failed, checking for legacy login...');
            await handleLegacyMigration();
          }
        } else {
          console.log('🔄 No unified auth found, checking for legacy login...');
          await handleLegacyMigration();
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        await handleLegacyMigration();
      } finally {
        setAuthLoading(false);
      }
    };

    // Handle legacy user migration
    const handleLegacyMigration = async () => {
      try {
        const migrationResult = await unifiedAuthService.migrateLegacyUser();

        if (migrationResult && migrationResult.success) {
          setUserProfile(migrationResult.user);
          setIsLoggedIn(true);
          setIsVerified(unifiedAuthService.hasAnyVerification());
          console.log('✅ Legacy user migrated successfully');
        } else {
          // Check for existing legacy login that couldn't be migrated
          const instagramUser = localStorage.getItem('instagramUser');
          const instagramToken = localStorage.getItem('instagramAccessToken');
          const savedUser = localStorage.getItem('instagram_user');
          const savedToken = localStorage.getItem('facebook_token');

          if ((instagramUser && instagramToken) || (savedUser && savedToken)) {
            try {
              const userProfile = JSON.parse(instagramUser || savedUser);
              setUserProfile(userProfile);
              setIsLoggedIn(true);
              setIsVerified(true);
              console.log('✅ Restored legacy session (migration pending)');
            } catch (error) {
              console.error('Error restoring legacy session:', error);
              // Clear invalid legacy data
              localStorage.removeItem('instagramUser');
              localStorage.removeItem('instagramAccessToken');
              localStorage.removeItem('instagram_user');
              localStorage.removeItem('facebook_token');
            }
          }
        }
      } catch (error) {
        console.error('Legacy migration error:', error);
      }
    };

    initializeAuth();
  }, []);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileDropdown && !event.target.closest('.profile-dropdown')) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown]);

  // Handle Instagram OAuth callback
  useEffect(() => {
    const handleInstagramCallback = async () => {
      // Prevent processing the same callback multiple times
      if (callbackProcessed) {
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');

      // Check if we're on the callback route with an authorization code
      if (window.location.pathname === '/auth/callback' && code) {
        try {
          // Check if this code has already been processed
          const processedCode = sessionStorage.getItem('processed_instagram_code');
          if (processedCode === code) {
            console.log('⚠️ Instagram code already processed, skipping...');
            window.history.replaceState({}, document.title, '/');
            return;
          }

          console.log('🔄 Processing Instagram callback with code:', code);

          // Mark as processed to prevent duplicate calls
          setCallbackProcessed(true);
          sessionStorage.setItem('processed_instagram_code', code);

          // Execute the complete Instagram login flow (steps 2, 3, 4)
          // Execute the complete Instagram login flow (steps 2, 3, 4)
          // (If your handleInstagramLoginFlow or other API functions use a base URL, pass API_BASE_URL)
          const userData = await handleInstagramLoginFlow(code, API_BASE_URL);
          // Example usage in fetch/axios:
          // fetch(`${API_BASE_URL}/api/instagram/profile/${username}`)
          // Replace all instances of 'http://localhost:3001' with API_BASE_URL in API calls below

          console.log('✅ Instagram login successful:', userData);

          // Update app state
          setUserProfile(userData);
          setIsLoggedIn(true);

          // Clean up URL and redirect to home
          window.history.replaceState({}, document.title, '/');

        } catch (error) {
          console.error('❌ Instagram callback error:', error);

          // Show user-friendly error message
          if (error.message.includes('already been used')) {
            alert('This login session has expired. Please try logging in again.');
          } else {
            alert(`Instagram login failed: ${error.message}`);
          }

          // Clean up URL on error
          window.history.replaceState({}, document.title, '/');

          // Reset processed flag on error so user can try again
          setCallbackProcessed(false);
          sessionStorage.removeItem('processed_instagram_code');
        }
      } else if (window.location.pathname === '/auth/callback' && error) {
        console.error('❌ Instagram authorization error:', error);
        alert(`Instagram authorization failed: ${error}`);

        // Clean up URL on error
        window.history.replaceState({}, document.title, '/');
      }
    };

    handleInstagramCallback();
  }, [callbackProcessed]);

  // Handle URL-based routing for privacy policy
  useEffect(() => {
    const handleUrlRouting = () => {
      const path = window.location.pathname;

      if (path === '/privacy_policy') {
        setCurrentPage('privacy_policy');
      } else if (path === '/' || path === '') {
        setCurrentPage('home');
      }
    };

    // Check initial URL
    handleUrlRouting();

    // Handle browser back/forward buttons
    const handlePopState = () => {
      handleUrlRouting();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Update URL when currentPage changes
  useEffect(() => {
    const currentPath = window.location.pathname;

    if (currentPage === 'privacy_policy' && currentPath !== '/privacy_policy') {
      window.history.pushState({}, 'Privacy Policy - IGReview', '/privacy_policy');
    } else if (currentPage === 'home' && currentPath !== '/' && currentPath !== '/privacy_policy') {
      window.history.pushState({}, 'IGReview', '/');
    }
  }, [currentPage]);

  // Facebook Login Functions
  const handleInstagramLogin = async () => {
    try {
      console.log('🔄 Starting Facebook login for Instagram access...');
      const result = await sdkInstagramLogin();

      if (result.success) {
        console.log('✅ Facebook login successful!');
        console.log('User Profile:', result.userProfile);
        console.log('Instagram Accounts:', result.instagramAccounts);

        // Update state with user profile
        setUserProfile(result.userProfile);
        setIsLoggedIn(true);

        // Close auth modal
        setShowAuthModal(false);
      }
    } catch (error) {
      console.error('❌ Facebook login error:', error);
      alert(`Facebook login failed: ${error.message}`);
    }
  };

  // Direct Instagram Login Functions
  const handleDirectInstagramLogin = async () => {
    try {
      console.log('🔄 Starting direct Instagram login...');

      // Close auth modal before redirect
      setShowAuthModal(false);

      // Initiate Instagram OAuth flow
      initiateInstagramLogin();
    } catch (error) {
      console.error('❌ Direct Instagram login error:', error);
      alert(`Instagram login failed: ${error.message}`);
    }
  };

  // Check if user is already logged in on page load
  const checkExistingLogin = async () => {
    try {
      const authResponse = await checkLoginStatus();
      if (authResponse) {
        console.log('✅ User already logged in');
        // You could automatically fetch their data here
      }
    } catch (error) {
      console.log('No existing login found');
    }
  };

  // Handle logout
  // Handle logout using unified system
  const handleLogout = async () => {
    try {
      // Use unified logout
      await unifiedAuthService.logout();
      console.log('✅ Unified logout successful');

      // Clear application state
      setIsLoggedIn(false);
      setIsVerified(false);
      setUserProfile(null);
      setUserReviews([]);
      setShowProfileDropdown(false);
      navigate('/'); // Navigate to home after logout

      // Also clear any legacy logout if needed
      try {
        if (userProfile?.loginMethod === 'instagram') {
          logoutInstagramUser();
        } else {
          await logout(); // Facebook logout
        }
      } catch (legacyError) {
        console.warn('Legacy logout warning:', legacyError);
        navigate('/'); // Navigate to home after logout
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Clear state anyway
      setIsLoggedIn(false);
      setIsVerified(false);
      setUserProfile(null);
      setUserReviews([]);
      setShowProfileDropdown(false);
      navigate('/'); // Navigate to home after logout
    }
  };

  // Handle email/phone verification (without password)
  const handleVerifyMe = async (contact, verificationCode) => {
    try {
      // Call verification endpoint
      const response = await fetch(`${API_BASE_URL}/api/verification-email-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact, verificationCode })
      });

      if (response.ok) {
        const userData = await response.json();
        setIsVerified(true);
        setUserProfile(userData.user);
        fetchUserReviews(userData.user.username || userData.user.email);
        setShowVerifyModal(false);
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.message || 'Verification failed' };
      }
    } catch (error) {
      console.error('Verification error:', error);
      return { success: false, error: 'Network error occurred' };
    }
  };

  // ===============================
  // UNIFIED AUTHENTICATION FUNCTIONS
  // ===============================

  // Handle email/phone registration using unified system
  const handleSignUp = async (userData) => {
    try {
      setAuthLoading(true);

      // Determine verification data based on signup method
      const verificationData = {};
      if (userData.email) verificationData.emailVerified = false; // Will be verified later
      if (userData.phone) verificationData.phoneVerified = false; // Will be verified later
      if (userData.instagramUsername) verificationData.instagramVerified = true; // Assume verified if from Instagram

      const signupData = {
        email: userData.email,
        phone: userData.phone,
        instagramUsername: userData.instagramUsername || userData.username,
        name: userData.name,
        familyName: userData.family || userData.familyName,
        password: userData.password, // Add password to signup data
        verificationData: verificationData,
        userAgent: navigator.userAgent
      };

      const result = await unifiedAuthService.signup(signupData);

      if (result.success) {
        setIsLoggedIn(true);
        setUserProfile(result.user);
        setIsVerified(unifiedAuthService.hasAnyVerification());
        setShowSignupModal(false);
        setShowAuthModal(false);

        // Fetch user reviews
        const reviewsResult = await unifiedAuthService.getUserReviews();
        if (reviewsResult.success) {
          setUserReviews(reviewsResult.reviews);
        }

        console.log('✅ Unified signup successful');
        return { success: true };
      } else {
        console.error('Unified signup failed:', result.error);

        // Handle "user already exists" case
        if (result.suggestion === 'signin') {
          setAuthMode('signin');
          setAuthError(result.message + ' Please sign in below.');
          return { success: false, error: result.message, switchToSignin: true };
        }

        return { success: false, error: result.error || result.message };
      }
    } catch (error) {
      console.error('Sign up error:', error);
      return { success: false, error: 'Registration failed' };
    } finally {
      setAuthLoading(false);
    }
  };

  // Handle email/phone sign in using unified system
  const handleSignIn = async (contact, password) => {
    try {
      setAuthLoading(true);

      // First clear any existing auth data to ensure clean state
      unifiedAuthService.clearStorage();
      setIsLoggedIn(false);
      setUserProfile(null);
      setUserReviews([]);
      setIsVerified(false);

      // Determine identifier type
      const credentials = {};
      if (contact.includes('@') && contact.includes('.')) {
        credentials.email = contact;
      } else if (contact.startsWith('+')) {
        credentials.phone = contact;
      } else {
        credentials.instagramUsername = contact.replace('@', '');
      }

      // Add password if provided (not implemented yet)
      if (password) {
        credentials.password = password;
      }

      const result = await unifiedAuthService.signin(credentials);

      if (result.success) {
        setIsLoggedIn(true);
        setUserProfile(result.user);
        setIsVerified(unifiedAuthService.hasAnyVerification());
        setShowAuthModal(false);

        // Fetch user reviews
        const reviewsResult = await unifiedAuthService.getUserReviews();
        if (reviewsResult.success) {
          setUserReviews(reviewsResult.reviews);
        }

        // navigate('/'); // Navigate to home after successful auth

        console.log('✅ Unified signin successful');
        return { success: true };
      } else {
        // Ensure we stay in clean state on failure
        unifiedAuthService.clearStorage();
        setIsLoggedIn(false);
        setUserProfile(null);
        setUserReviews([]);
        setIsVerified(false);
        // navigate('/'); // Navigate to home after successful auth

        console.error('Unified signin failed:', result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      // Ensure we stay in clean state on error
      unifiedAuthService.clearStorage();
      setIsLoggedIn(false);
      setUserProfile(null);
      setUserReviews([]);
      setIsVerified(false);

      console.error('Sign in error:', error);
      return { success: false, error: 'Sign in failed' };
    } finally {
      setAuthLoading(false);
      navigate('/'); // Navigate to home after successful auth
    }
  };

  // Fetch user's reviews using unified system
  const fetchUserReviews = async (username) => {
    try {
      if (unifiedAuthService.isAuthenticated()) {
        // Use authenticated endpoint for current user
        const result = await unifiedAuthService.getUserReviews();
        if (result.success) {
          setUserReviews(result.reviews);
          return;
        }
      }

      // Fallback to public endpoint
      const response = await fetch(`${API_BASE_URL}/api/get-business-reviews?username=${encodeURIComponent(username)}&mode=reviewer`);
      if (response.ok) {
        const data = await response.json();
        setUserReviews(data.reviews || []);
      }
    } catch (error) {
      console.error('Error fetching user reviews:', error);
      setUserReviews([]);
    }
  };

  // Replace with the version of this func in QuickReviewFlow.js 
  // Handle user verification lookup (Instagram)
  // 
  const handleUserVerificationLookup = async (username) => {
    if (!username || username.length < 3) return;

    const cleanUsername = username.startsWith('@') ? username.slice(1) : username;

    try {
      setIsLookingUpUser(true);
      setLookupError('');

      const response = await fetch(`${API_BASE_URL}/api/instagram/search_profile?username=${encodeURIComponent(cleanUsername)}`);

      if (!response.ok) {
        throw new Error('User not found');
      }

      const data = await response.json();

      if (data.user) {
        const user = {
          username: `@${data.user.username}`,
          avatar: data.user.username.charAt(0).toUpperCase(),
          verified: data.user.is_verified,
          followers: data.user.followers || 0,
          searchData: data.user
        };

        setVerifyUsername(user);

        // Generate verification code for Instagram verification
        const code = Math.random().toString(36).substring(2, 8).toUpperCase();
        setGeneratedCode(code);

        setLookupError('');
      } else {
        throw new Error('User not found');
      }
    } catch (error) {
      console.error('Instagram lookup error:', error);
      setLookupError(error.message || 'Failed to find user');
      setVerifyUsername(null);
      setGeneratedCode('');
    } finally {
      setIsLookingUpUser(false);
    }
  };

  // Reset verification state
  const resetVerificationState = () => {
    setVerificationMethod(null);
    setVerifyUsername(null);
    setPhoneNumber('');
    setEmailAddress('');
    setPhoneVerificationCode('');
    setEmailVerificationCode('');
    setIsUserVerified(false);
    setIsPhoneVerified(false);
    setIsEmailVerified(false);
    setSkippedVerification(false);
    setGeneratedCode('');
    setLookupError('');
  };



  // Save user data when it changes
  useEffect(() => {
    localStorage.setItem('bookmarkedServices', JSON.stringify(bookmarkedServices));
  }, [bookmarkedServices]);

  useEffect(() => {
    localStorage.setItem('followedProviders', JSON.stringify(followedProviders));
  }, [followedProviders]);

  // Enhanced Leaderboard Page
  const LeaderboardPage = () => {
    const sortedServices = [...filteredServices].sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'reviews':
          return b.reviewCount - a.reviewCount;
        case 'followers':
          return parseFloat(b.followers.replace(/[KM]/g, '')) - parseFloat(a.followers.replace(/[KM]/g, ''));
        default:
          return 0;
      }
    });

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold mb-4">🏆 Top Rated Services</h1>
            <p className="text-xl text-purple-100 max-w-2xl mx-auto">
              Discover the highest-rated Instagram service providers based on authentic customer reviews
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white border-b border-gray-200 py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              {/* View Tabs */}
              <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
                {[
                  { id: 'overall', label: 'Overall', icon: Trophy },
                  { id: 'category', label: 'By Category', icon: Target },
                  { id: 'region', label: 'By Region', icon: MapPin }
                ].map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setLeaderboardView(tab.id)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${leaderboardView === tab.id
                        ? 'bg-white shadow-sm text-purple-600 font-medium'
                        : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                      <Icon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Sort Options */}
              <div className="flex items-center space-x-4">
                <span className="text-gray-600 text-sm">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="rating">Highest Rating</option>
                  <option value="reviews">Most Reviews</option>
                  <option value="followers">Most Followers</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Leaderboard Content */}
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Top 3 Podium */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-center mb-8">🥇 Top Performers</h2>
              <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                {sortedServices.slice(0, 3).map((service, index) => (
                  <div
                    key={service.id}
                    className={`relative bg-white rounded-xl p-6 text-center transform hover:scale-105 transition-all ${index === 0 ? 'ring-4 ring-yellow-400 shadow-xl' :
                      index === 1 ? 'ring-4 ring-gray-400 shadow-lg' :
                        'ring-4 ring-orange-400 shadow-lg'
                      }`}
                  >
                    {/* Medal */}
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${index === 0 ? 'bg-yellow-500' :
                        index === 1 ? 'bg-gray-500' :
                          'bg-orange-500'
                        }`}>
                        {index + 1}
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">
                        {service.avatar}
                      </div>
                      <h3 className="font-bold text-lg text-gray-900 mb-1">{service.name}</h3>
                      <p className="text-purple-600 text-sm font-medium mb-3">{service.username}</p>

                      <div className="flex items-center justify-center space-x-1 mb-2">
                        <Star className="text-yellow-400" size={16} fill="currentColor" />
                        <span className="font-bold text-gray-900">{service.rating}</span>
                        <span className="text-gray-500 text-sm">({service.reviewCount})</span>
                      </div>

                      <div className="text-sm text-gray-600 mb-4">
                        {service.followers} followers
                      </div>

                      <button
                        onClick={() => openServiceProfile(service)}
                        className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 transition-colors font-medium"
                      >
                        View Profile
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Detailed Rankings */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Complete Rankings</h3>
              </div>

              <div className="divide-y divide-gray-200">
                {sortedServices.map((service, index) => (
                  <div key={service.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full font-bold text-gray-700">
                          {index + 1}
                        </div>

                        <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-xl">
                          {service.avatar}
                        </div>

                        <div>
                          <div className="flex items-center space-x-2">
                            <h4 className="font-semibold text-gray-900">{service.name}</h4>
                            {service.verified && <Verified className="text-blue-500" size={16} />}
                            {service.featured && <Crown className="text-yellow-500" size={16} />}
                          </div>
                          <p className="text-purple-600 text-sm">{service.username}</p>
                          <p className="text-gray-600 text-sm">{service.title}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-8">
                        <div className="text-center">
                          <div className="flex items-center space-x-1">
                            <Star className="text-yellow-400" size={16} fill="currentColor" />
                            <span className="font-bold text-gray-900">{service.rating}</span>
                          </div>
                          <div className="text-xs text-gray-500">{service.reviewCount} reviews</div>
                        </div>

                        <div className="text-center">
                          <div className="font-semibold text-gray-900">{service.followers}</div>
                          <div className="text-xs text-gray-500">followers</div>
                        </div>

                        <div className="text-center">
                          <div className="font-semibold text-purple-600">{service.price}</div>
                          <div className="text-xs text-gray-500">per session</div>
                        </div>

                        <button
                          onClick={() => openServiceProfile(service)}
                          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
                        >
                          View Profile
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Enhanced How It Works Page
  const HowItWorksPage = () => (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">How IGReview Works</h1>
          <p className="text-xl text-purple-100 mb-8">
            Your complete guide to finding, reviewing, and connecting with trusted Instagram service providers
          </p>
        </div>
      </div>

      {/* Process Steps */}
      <div className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                step: 1,
                title: 'Discover Services',
                icon: Search,
                description: 'Browse our curated directory of verified Instagram service providers. Use smart filters to find exactly what you need.',
                features: ['Smart search & filters', 'Verified providers only', 'Real customer reviews', 'Detailed profiles']
              },
              {
                step: 2,
                title: 'Read Reviews',
                icon: MessageCircle,
                description: 'Read authentic reviews from real customers. Our verification system ensures review authenticity.',
                features: ['Verified reviews only', 'Service vs. ad distinction', 'Regional insights', 'Detailed ratings']
              },
              {
                step: 3,
                title: 'Connect & Share',
                icon: Users,
                description: 'Connect with providers and share your own experiences to help the community make informed decisions.',
                features: ['Direct provider contact', 'Write verified reviews', 'Community discussions', 'Service recommendations']
              }
            ].map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="text-center group">
                  <div className="relative mb-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="text-white" size={32} />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                      {step.step}
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{step.description}</p>

                  <ul className="space-y-2">
                    {step.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center justify-center space-x-2 text-sm text-gray-700">
                        <CheckCircle className="text-green-500" size={16} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600">Everything you need to know about using IGReview</p>
          </div>

          <div className="space-y-6">
            {[
              {
                question: 'How do you verify reviews?',
                answer: 'We use a multi-step verification process including Instagram authentication, transaction verification, and AI-powered authenticity checks to ensure all reviews are from real customers.'
              },
              {
                question: 'What\'s the difference between service and advertisement reviews?',
                answer: 'Service reviews are for direct services like coaching or consulting. Advertisement reviews are for products or brands promoted by influencers. We clearly distinguish between both types.'
              },
              {
                question: 'How do providers get verified?',
                answer: 'Providers undergo identity verification, business validation, and maintain high-quality service standards to earn and keep their verified status.'
              },
              {
                question: 'Is IGReview free to use?',
                answer: 'Yes! IGReview is completely free for users to browse, read reviews, and write reviews. Providers may pay for enhanced features and visibility.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-purple-100 mb-8">
            Join thousands of users who trust IGReview for their service provider decisions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setCurrentPage('home')}
              className="bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
            >
              Browse Services
            </button>
            <button
              onClick={() => setShowQuickReview(true)}
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-purple-600 transition-all"
            >
              Write Your First Review
            </button>
          </div>
        </div>
      </div>
    </div>
  );


  // Update setCurrentPage calls to use navigate instead
  const navigateToPage = (page) => {
    switch (page) {
      case 'home':
        navigate('/');
        break;
      case 'myprofile':
        navigate('/myprofile');
        break;
      case 'leaderboard':
        navigate('/leaderboard');
        break;
      case 'how-it-works':
        navigate('/how-it-works');
        break;
      case 'privacy_policy':
        navigate('/privacy-policy');
        break;
      default:
        navigate('/');
    }
  };
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''}`}>
      <Header
        showQuickActions={showQuickActions}
        setShowQuickActions={setShowQuickActions}
        setShowQuickReview={setShowQuickReview}
        setShowProviderSystem={setShowProviderSystem}
        isLoggedIn={isLoggedIn}
        isVerified={isVerified}
        userProfile={userProfile}
        showProfileDropdown={showProfileDropdown}
        setShowProfileDropdown={setShowProfileDropdown}
        setShowUserReviews={setShowUserReviews}
        handleLogout={handleLogout}
        setShowVerifyModal={setShowVerifyModal}
        setShowAuthModal={setShowAuthModal}
        setAuthMode={setAuthMode}
        userReviews={userReviews}
      />

      {/* Development Mode Verification Code Notification */}
      {verificationNotification && (
        <VerificationNotification
          code={verificationNotification.code}
          type={verificationNotification.type}
          onClose={() => setVerificationNotification(null)}
        />
      )}

      <main>
        <Routes>
          {/* Home Route */}
          <Route
            path="/"
            element={
              <HomePage
                isLoggedIn={isLoggedIn}
                isVerified={isVerified}
                userReviews={userReviews}
                setShowQuickReview={setShowQuickReview}
                activeFilters={activeFilters}
                searchQuery={searchQuery}
                filteredServices={filteredServices}
                setActiveFilters={setActiveFilters}
                setSearchQuery={setSearchQuery}

                openServiceProfile={openServiceProfile}
                startReviewFlow={startReviewFlow}
                bookmarkedServices={bookmarkedServices}
                setBookmarkedServices={setBookmarkedServices}
                followedProviders={followedProviders}
                setFollowedProviders={setFollowedProviders}
                handleServiceBookmark={handleServiceBookmark}
                handleProviderFollow={handleProviderFollow}
              />
            }
          />

          {/* My Profile Route */}
          <Route
            path="/myprofile"
            element={
              <MyProfilePage
                userProfile={userProfile}
                onClose={() => navigate('/')} // Navigate back to home
                onUpdate={async (formData) => {
                  // Your existing update logic
                  setAuthLoading(true);
                  let result = { success: false };
                  try {
                    const response = await fetch(`${API_BASE_URL}/api/auth-unified`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        action: 'update',
                        userId: userProfile?.userId,
                        ...formData
                      })
                    });
                    const data = await response.json();
                    if (response.ok && data.user) {
                      setUserProfile(data.user);
                      result = { success: true };
                    } else {
                      result = { success: false, error: data.error || 'Failed to update profile' };
                    }
                  } catch (error) {
                    result = { success: false, error: error.message };
                  } finally {
                    setAuthLoading(false);
                  }
                  return result;
                }}
                loading={authLoading}
                error={authError}
              />
            }
          />

          <Route
            path="/myreviews"
            element={<MyReviewsPage
              isLoggedIn={isLoggedIn}
              isVerified={isVerified}
              userReviews={userReviews}
              setShowQuickReview={setShowQuickReview}
            />}
          />

          {/* Leaderboard Route */}
          <Route
            path="/leaderboard"
            element={<LeaderboardPage />}
          />

          {/* How It Works Route */}
          <Route
            path="/how-it-works"
            element={<HowItWorksPage />}
          />

          {/* Privacy Policy Route */}
          <Route
            path="/privacy-policy"
            element={<PrivacyPolicy onBack={() => navigate('/')} />}
          />

          {/* Catch-all route - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* All your existing modals and overlays remain the same */}
      {showProfileModal && (
        <ProfileModal
          selectedProfile={selectedProfile}
          showProfileModal={showProfileModal}
          setShowProfileModal={setShowProfileModal}
          activeProfileTab={activeProfileTab}
          setActiveProfileTab={setActiveProfileTab}
          startReviewFlow={startReviewFlow}
          handleServiceBookmark={handleServiceBookmark}
          bookmarkedServices={bookmarkedServices}
          reviews={profileReviews}
        />
      )}

      {/* Enhanced Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Sign In
              </h2>
              <button
                onClick={() => {
                  setShowAuthModal(false);
                  setAuthError('');
                  setAuthData({ contact: '', password: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {authError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{authError}</p>
              </div>
            )}

            {/* Social Login Options */}
            <div className="space-y-3 mb-6">
              <button
                onClick={handleInstagramLogin}
                className="w-full flex items-center justify-center space-x-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-lg font-medium hover:shadow-lg transition-all"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                <span>Continue with Facebook</span>
              </button>

              <button
                onClick={handleDirectInstagramLogin}
                className="w-full flex items-center justify-center space-x-3 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white py-3 rounded-lg font-medium hover:shadow-lg transition-all"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.645.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.204.014-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
                <span>Continue with Instagram</span>
              </button>
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with email/phone</span>
              </div>
            </div>

            {/* Sign In Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email or Phone
                </label>
                <input
                  type="text"
                  value={authData.contact}
                  onChange={(e) => setAuthData({ ...authData, contact: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter your email or phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={authData.password}
                  onChange={(e) => setAuthData({ ...authData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter your password"
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300" />
                  <span className="ml-2 text-sm text-gray-600">Remember me</span>
                </label>
                <button className="text-sm text-purple-600 hover:text-purple-700">
                  Forgot password?
                </button>
              </div>

              <button
                onClick={async () => {
                  if (!authData.contact || !authData.password) {
                    setAuthError('Please enter both email/phone and password');
                    return;
                  }

                  setAuthLoading(true);
                  setAuthError('');
                  const result = await handleSignIn(authData.contact, authData.password);
                  setAuthLoading(false);
                  if (!result.success) {
                    setAuthError(result.error);
                  }
                }}
                disabled={authLoading}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50"
              >
                {authLoading ? 'Signing In...' : 'Sign In'}
              </button>

              <div className="text-center">
                <span className="text-gray-600">Don't have an account? </span>
                <button
                  onClick={() => {
                    setShowSignupModal(true);
                    setShowAuthModal(false);
                    setAuthError('');
                  }}
                  className="text-purple-600 hover:text-purple-700 font-medium"
                >
                  Sign up
                </button>
              </div>
            </div>

            {/* Info Message */}
            <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <Info size={16} className="text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Account Benefits:</p>
                  <ul className="mt-1 text-xs text-blue-700">
                    <li>• Write and manage your reviews</li>
                    <li>• Access exclusive verified provider features</li>
                    <li>• Get personalized service recommendations</li>
                    <li>• Join the trusted IGReview community</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Verify Me Modal (Case 7 Implementation) */}
      {showVerifyModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full relative">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              onClick={() => setShowVerifyModal(false)}
            >
              <X size={24} />
            </button>
            <VerificationSteps
              verificationMethod={verificationMethod}
              setVerificationMethod={setVerificationMethod}
              verifyUsername={verifyUsername}
              setVerifyUsername={setVerifyUsername}
              generatedCode={generatedCode}
              setGeneratedCode={setGeneratedCode}
              isUserVerified={isUserVerified}
              setIsUserVerified={setIsUserVerified}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              phoneVerificationCode={phoneVerificationCode}
              setPhoneVerificationCode={setPhoneVerificationCode}
              isPhoneVerified={isPhoneVerified}
              setIsPhoneVerified={setIsPhoneVerified}
              emailAddress={emailAddress}
              setEmailAddress={setEmailAddress}
              emailVerificationCode={emailVerificationCode}
              setEmailVerificationCode={setEmailVerificationCode}
              isEmailVerified={isEmailVerified}
              setIsEmailVerified={setIsEmailVerified}
              skippedVerification={skippedVerification}
              setSkippedVerification={setSkippedVerification}
              isVerified={isVerified}
              setIsVerified={setIsVerified}
              userProfile={userProfile}
              setUserProfile={setUserProfile}
              lookupError={lookupError}
              setLookupError={setLookupError}
              isLookingUpUser={isLookingUpUser}
              setIsLookingUpUser={setIsLookingUpUser}
              setVerificationNotification={setVerificationNotification}
              fetchUserReviews={fetchUserReviews}
              setShowVerifyModal={setShowVerifyModal}
              setAuthError={setAuthError}
              isSavingReview={isSavingReview}
              reviewSaveSuccess={reviewSaveSuccess}
            />
          </div>
        </div>
      )}

      {/* Sign Up Page (Full Screen) */}
      {showSignupModal && (
        <SignUpPage
          onClose={() => setShowSignupModal(false)}
          onSignUp={handleSignUp}
          loading={authLoading}
          error={authError}
          onSwitchToSignIn={() => {
            setShowSignupModal(false);
            setShowAuthModal(true);
            setAuthMode('signin');
            setAuthError('');
          }}
        />
      )}

      {showQuickReview && (
        <QuickReviewFlow
          service={selectedServiceForReview}
          preSelectedBusiness={selectedServiceForReview}
          // serviceTypes={serviceTypes}
          isLoggedIn={isLoggedIn}
          isVerified={isVerified || isEmailVerified || isPhoneVerified}
          userProfile={userProfile}
          setUserProfile={setUserProfile}
          setVerificationNotification={setVerificationNotification}
          verificationProfile={
            isVerified ? {
              email: isEmailVerified ? emailAddress : undefined,
              phone: isPhoneVerified ? phoneNumber : undefined,
              instagram: verifyUsername
            } : null
          }
          onClose={() => {
            setShowQuickReview(false);
            setSelectedServiceForReview(null);
          }}
          onComplete={async (reviewData) => {
            console.log('✅ Review submission completed:', reviewData);

            // Handle authentication if user was verified during review process
            if (reviewData.shouldAuthenticate && reviewData.userProfile) {
              console.log('🔐 User authenticated during review, logging them in:', reviewData.userProfile);

              // Try unified authentication flow
              try {
                let authSuccess = false;
                let finalUserProfile = reviewData.userProfile;

                // First, try to find if user already exists by attempting signin
                if (reviewData.userProfile.email) {
                  console.log('🔄 Checking if user exists with email signin...');
                  const signinResult = await unifiedAuthService.signin({
                    email: reviewData.userProfile.email
                  });

                  if (signinResult.success) {
                    console.log('✅ User signed in successfully:', signinResult.user);
                    finalUserProfile = signinResult.user;
                    authSuccess = true;
                  } else {
                    console.log('👤 User does not exist, creating new account...');
                  }
                }

                // If signin failed, try creating new user
                if (!authSuccess) {
                  const signupData = {
                    email: reviewData.userProfile.email,
                    name: reviewData.userProfile.name,
                    familyName: reviewData.userProfile.familyName,
                    verificationData: {
                      emailVerified: reviewData.userProfile.verificationStatus?.email || true,
                      phoneVerified: reviewData.userProfile.verificationStatus?.phone || false,
                      instagramVerified: reviewData.userProfile.verificationStatus?.instagram || false
                    }
                  };

                  console.log('🔄 Creating new user account...');
                  const signupResult = await unifiedAuthService.signup(signupData);

                  if (signupResult.success) {
                    console.log('✅ User account created successfully:', signupResult.user);
                    finalUserProfile = signupResult.user;
                    authSuccess = true;
                  } else {
                    console.log('❌ Account creation failed:', signupResult.error);
                  }
                }

                if (authSuccess) {
                  // Use unified system authentication
                  setIsLoggedIn(true);
                  setIsVerified(true);
                  setUserProfile(finalUserProfile);
                  console.log('✅ User authenticated via unified system');
                } else {
                  // Fallback to local authentication
                  console.log('⚠️ Unified auth failed, using local authentication');
                  setIsLoggedIn(true);
                  setIsVerified(true);
                  setUserProfile(reviewData.userProfile);
                }

              } catch (error) {
                console.warn('🚨 Unified authentication error, using local authentication:', error);
                // Fallback to local authentication
                setIsLoggedIn(true);
                setIsVerified(true);
                setUserProfile(reviewData.userProfile);
              }

              // Navigate to home page and show their reviews
              setCurrentPage('home');
              setShowUserReviews(true);
            }

            // Refresh user reviews using unified system
            if (unifiedAuthService.isAuthenticated()) {
              const reviewsResult = await unifiedAuthService.getUserReviews();
              if (reviewsResult.success) {
                setUserReviews(reviewsResult.reviews);
                console.log('✅ User reviews refreshed after submission');
              }
            } else if (reviewData.emailAddress || reviewData.user?.username) {
              // Fallback for non-authenticated users - fetch reviews by identifier
              const reviewerIdentifier = reviewData.emailAddress || reviewData.user?.username;
              await fetchUserReviews(reviewerIdentifier);
            }

            // Update user verification status if they completed verification during review
            if (reviewData.isVerified || reviewData.isEmailVerified || reviewData.isPhoneVerified || reviewData.isUserVerified) {
              setIsVerified(true);
            }
          }}
          showVerifyMe={!isLoggedIn && !isVerified}
        />
      )}

      {showReviewSystem && (
        <ReviewSystem
          onClose={() => setShowReviewSystem(false)}
          selectedService={selectedServiceForReview}
          isLoggedIn={isLoggedIn}
          userProfile={userProfile}
          onLoginRequired={() => {
            setShowReviewSystem(false);
            setShowAuthModal(true);
            setAuthMode('signin');
          }}
        />
      )}

      {showProviderSystem && (
        <ServiceProviderSystem
          onClose={() => setShowProviderSystem(false)}
        />
      )}

      {/* Footer - update navigation */}
      <footer className="bg-gray-50 border-t border-gray-200 py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">IG</span>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                IGReview
              </span>
            </div>
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
              <button
                onClick={() => navigate('/privacy-policy')}
                className="hover:text-purple-600 transition-colors"
              >
                Privacy Policy
              </button>
              <span className="text-gray-400">•</span>
              <span>© 2024 IGReview. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;